import React, { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useDropzone } from "react-dropzone";
import {
  Upload,
  FileText,
  Trash2,
  RefreshCw,
  Search,
  Filter,
  MoreVertical,
  Download,
  Eye,
  CheckCircle,
  AlertCircle,
  Clock,
  Database,
  MessageSquare,
  Send,
  Bot,
  User,
  Sparkles,
  Brain,
  X,
  Building2,
  ChevronDown,
  Check,
  Info,
} from "lucide-react";
import { useKnowledge } from "../context/KnowledgeContext";
import {
  useSuccessToast,
  useErrorToast,
  useInfoToast,
} from "../../../foundation/altzero_base/base/components/ui/toast";
import { Progress } from "../../../foundation/altzero_base/base/components/ui/progress";
import { knowledgeService } from "../services/knowledgeService";
import { Document, DocumentStatus } from "../types/knowledge";
import {
  KNOWLEDGE_TOAST_MESSAGES,
  DEFAULT_SYSTEM_MESSAGE,
} from "../utils/constants";

interface ChatMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: string;
  sources?: any[];
  metadata?: {
    uniqueDocuments?: number;
    documentsUsed?: string[];
    retrievedDocuments?: number;
    processingTime?: number;
    model?: string;
    hasValidContext?: boolean;
  };
}

const AIAssistant: React.FC = () => {
  const {
    state: { documents, uploadProgress, stats, isUploading, error },
    setDocuments,
    addDocument,
    setUploadProgress,
    clearUploadProgress,
    setStats,
    setUploading,
    setError,
    clearError,
    updateDocument,
    deleteDocument: removeDocument,
  } = useKnowledge();

  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<DocumentStatus | "all">(
    "all"
  );
  const [selectedDocuments, setSelectedDocuments] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<"upload" | "chat">("upload");
  const [showUploadDialog, setShowUploadDialog] = useState(false);

  // Upload form state
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>("");
  const [documentDescription, setDocumentDescription] =
    useState<string>("");
  const [documentCategory, setDocumentCategory] = useState<string>("");
  const [documentSubCategory, setDocumentSubCategory] = useState<string>("");
  const [companies, setCompanies] = useState<any[]>([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [companySearchQuery, setCompanySearchQuery] = useState<string>("");
  const [uploadCancelled, setUploadCancelled] = useState(false);
  const [uploadController, setUploadController] = useState<AbortController | null>(null);
  const [autoCloseTimer, setAutoCloseTimer] = useState<NodeJS.Timeout | null>(null);

  // Chat state
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [inputMessage, setInputMessage] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [chatError, setChatError] = useState<string | null>(null);

  const showSuccess = useSuccessToast();
  const showError = useErrorToast();
  const showInfo = useInfoToast();

  // Cancel upload function
  const cancelUpload = useCallback(() => {
    if (uploadController) {
      uploadController.abort();
      setUploadController(null);
    }
    setUploadCancelled(true);
    setUploading(false);

    // Clear all upload progress
    if (uploadProgress) {
      Object.keys(uploadProgress).forEach((id) => {
        clearUploadProgress(id);
      });
    }

    showInfo("Upload cancelled", "File upload has been cancelled");

    // Reset form state
    setDocumentDescription("");
    setUploadCancelled(false);
  }, [uploadController, uploadProgress, clearUploadProgress, showInfo]);

  // Close dialog function
  const closeDialog = useCallback(() => {
    // Clear auto-close timer if it exists
    if (autoCloseTimer) {
      clearTimeout(autoCloseTimer);
      setAutoCloseTimer(null);
    }

    // Cancel any ongoing uploads
    if (isUploading) {
      cancelUpload();
    }

    // Clear upload progress after a short delay
    setTimeout(() => {
      if (uploadProgress) {
        Object.keys(uploadProgress).forEach((id) => {
          clearUploadProgress(id);
        });
      }
    }, 500);

    setShowUploadDialog(false);
    setCompanySearchQuery("");
  }, [autoCloseTimer, isUploading, cancelUpload, uploadProgress, clearUploadProgress]);

  // Auto-close dialog after successful upload
  const scheduleAutoClose = useCallback(() => {
    const timer = setTimeout(() => {
      showSuccess("Upload completed", "All files uploaded successfully!");
      setTimeout(() => {
        closeDialog();
      }, 1500); // 1.5 second delay after success message
    }, 1000); // 1 second delay before showing success message

    setAutoCloseTimer(timer);
  }, [showSuccess, closeDialog]);

  // Load documents and companies on mount
  useEffect(() => {
    loadDocuments();
    loadCompanies();
  }, []);

  // Watch for document changes and update stats
  useEffect(() => {
    if (documents && documents.length >= 0) {
      loadStats();
    }
  }, [documents]);

  const loadDocuments = async (): Promise<void> => {
    try {
      setIsLoading(true);
      console.log("🔍 Loading documents...");

      const docs = await knowledgeService.getDocuments();
      console.log("📄 Received documents:", docs);

      // Ensure docs is always an array and has proper structure
      const validDocs = Array.isArray(docs)
        ? docs.filter(
            (doc) => doc && typeof doc === "object" && doc.id && doc.name
          )
        : [];

      console.log("✅ Valid documents:", validDocs.length);
      setDocuments(validDocs);
    } catch (error) {
      console.error("❌ Failed to load documents:", error);
      showError(
        "Failed to load documents",
        "Please refresh the page and try again."
      );
      // Set empty array on error
      setDocuments([]);
    } finally {
      setIsLoading(false);
    }
  };

  const loadStats = async (): Promise<void> => {
    try {
      console.log("📊 Calculating stats from documents...");

      // Calculate stats from documents instead of API call
      const docs = documents || [];
      const statsData = {
        totalDocuments: docs.length,
        totalSize: docs.reduce((sum, doc) => sum + (doc.size || 0), 0),
        processingCount: docs.filter((doc) => doc.status === "processing")
          .length,
        errorCount: docs.filter((doc) => doc.status === "error").length,
        lastUpdated: new Date().toISOString(),
      };

      console.log("📈 Calculated stats:", statsData);
      setStats(statsData);
    } catch (error) {
      console.error("❌ Failed to calculate stats:", error);
      // Set default stats on error
      setStats({
        totalDocuments: 0,
        totalSize: 0,
        processingCount: 0,
        errorCount: 0,
        lastUpdated: new Date().toISOString(),
      });
    }
  };

  const loadCompanies = async (): Promise<void> => {
    try {
      setLoadingCompanies(true);

      // Import supabase client and get current user
      const { supabase } = await import("../../../foundation/altzero_base/base/utils/supabaseClient");
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (!user) {
        console.warn("❌ No authenticated user found");
        showError("Authentication required", "Please log in to view companies");
        return;
      }

      // Fetch companies from CRM filtered by owner_id (user_id)
      const { data: companiesData, error } = await supabase
        .from("crm_companies")
        .select("id, name")
        .eq("owner_id", user.id)
        .order("name");

      if (error) {
        console.error("❌ Failed to load companies:", error);
        showError("Failed to load companies", error.message);
        return;
      }

      setCompanies(companiesData || []);
      console.log(
        `🏢 Companies loaded for user ${user.id}:`,
        companiesData?.length || 0
      );
    } catch (error) {
      console.error("❌ Failed to load companies:", error);
      showError("Failed to load companies", "Please try again");
    } finally {
      setLoadingCompanies(false);
    }
  };

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      if (acceptedFiles.length === 0) return;

      // Company selection is now optional - use "default" if not selected
      const companyId = selectedCompanyId || "default";

      setUploading(true);
      setUploadCancelled(false);
      clearError();

      // Create abort controller for cancellation
      const controller = new AbortController();
      setUploadController(controller);

      try {
        // Validate files
        const validFiles: File[] = [];
        const invalidFiles: string[] = [];

        acceptedFiles.forEach((file) => {
          const validation = knowledgeService.validateFile(file);
          if (validation.isValid) {
            validFiles.push(file);
          } else {
            invalidFiles.push(`${file.name}: ${validation.error}`);
          }
        });

        if (invalidFiles.length > 0) {
          showError("Some files were rejected", invalidFiles.join("\n"));
        }

        if (validFiles.length === 0) {
          setUploading(false);
          setUploadController(null);
          return;
        }

        showInfo(
          KNOWLEDGE_TOAST_MESSAGES.UPLOAD_PROCESSING,
          `Processing ${validFiles.length} file(s)...`
        );

        // Upload files with streaming progress and metadata
        const response = await knowledgeService.uploadDocuments(
          validFiles,
          (progressData) => {
            // Update progress for each file
            Object.entries(progressData).forEach(([documentId, progress]) => {
              setUploadProgress({
                documentId,
                progress: progress.progress,
                status: progress.status,
                message: progress.message,
              });
            });
          },
          {
            companyId: companyId, // Use the companyId variable (default or selected)
            aiExtractionInstructions:
              documentDescription.trim() || undefined,
            category: documentCategory.trim() || "general",
            subCategory: documentSubCategory.trim() || "uncategorized",
          }
        );

        // Handle successful uploads
        if (response.documents && Array.isArray(response.documents)) {
          console.log("📄 Processing uploaded documents:", response.documents);

          response.documents.forEach((doc) => {
            // Validate document object before adding
            if (doc && doc.id && doc.name && doc.type && doc.uploadedAt) {
              console.log("✅ Adding valid document:", doc.name);
              // Clear progress and add document
              clearUploadProgress(doc.id);
              addDocument(doc);
            } else {
              console.warn("⚠️ Invalid document object received:", doc);
            }
          });

          showSuccess(
            KNOWLEDGE_TOAST_MESSAGES.UPLOAD_SUCCESS,
            `Successfully uploaded ${response.documents.length} document(s)`
          );

          // Clear form after successful upload
          setDocumentDescription("");
          setDocumentCategory("");
          setDocumentSubCategory("");
          // Keep company selected for convenience

          // Force reload documents to ensure consistency (awaited, no setTimeout)
          await loadDocuments();

          // Schedule auto-close if all uploads were successful
          if (!response.errors || response.errors.length === 0) {
            scheduleAutoClose();
          }
        } else {
          console.warn("⚠️ No documents in upload response:", response);
        }

        // Handle errors
        if (response.errors && response.errors.length > 0) {
          const errorMessages = response.errors.map(
            (err) => `${err.fileName}: ${err.error}`
          );
          showError("Some uploads failed", errorMessages.join("\n"));
        }

        // Recalculate stats
        setTimeout(() => loadStats(), 100);
      } catch (error) {
        console.error("Upload failed:", error);

        // Check if error is due to cancellation
        if (error instanceof Error && error.name === 'AbortError') {
          console.log("Upload was cancelled by user");
          return; // Don't show error for cancelled uploads
        }

        showError(
          KNOWLEDGE_TOAST_MESSAGES.UPLOAD_ERROR,
          error instanceof Error ? error.message : "Unknown error"
        );
      } finally {
        setUploading(false);
        setUploadController(null);
      }
    },
    [
      selectedCompanyId,
      documentDescription,
      setUploading,
      clearError,
      setUploadProgress,
      clearUploadProgress,
      addDocument,
      setStats,
      showSuccess,
      showError,
      showInfo,
      setUploadController,
      setUploadCancelled,
      scheduleAutoClose
    ]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      // PDF files
      "application/pdf": [".pdf"],

      // Word documents
      "application/msword": [".doc"],
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],

      // Text files
      "text/plain": [".txt"],

      // Markdown files (multiple MIME types for compatibility)
      "text/markdown": [".md"],
      "text/x-markdown": [".md"],
      "application/x-markdown": [".md"],

      // RTF files
      "application/rtf": [".rtf"],

      // OpenDocument Text
      "application/vnd.oasis.opendocument.text": [".odt"],
    },
    multiple: true,
    disabled: isUploading,
    // Allow files to be accepted even if MIME type doesn't match exactly
    validator: (file) => {
      const validExtensions = ['.pdf', '.doc', '.docx', '.txt', '.md', '.rtf', '.odt'];
      const fileExtension = '.' + file.name.toLowerCase().split('.').pop();

      if (validExtensions.includes(fileExtension)) {
        return null; // Valid file
      }

      return {
        code: "file-invalid-type",
        message: `File type not supported. Supported formats: PDF, Word (.doc/.docx), Text (.txt), Markdown (.md), RTF, ODT.`
      };
    }
  });

  const handleDeleteDocument = async (documentId: string): Promise<void> => {
    try {
      // Show confirmation dialog
      if (
        !window.confirm(
          "Are you sure you want to delete this document? This action cannot be undone."
        )
      ) {
        return;
      }

      console.log(`🗑️ Deleting document: ${documentId}`);

      // Call the backend to delete from Pinecone and other storage
      await knowledgeService.deleteDocument(documentId);

      // Remove from local state
      removeDocument(documentId);
      setSelectedDocuments((prev) => prev.filter((id) => id !== documentId));

      showSuccess(
        "Document deleted successfully",
        "The document has been removed from your knowledge base."
      );

      // Reload documents to ensure consistency
      setTimeout(() => {
        loadDocuments();
        loadStats();
      }, 500);
    } catch (error) {
      console.error("Failed to delete document:", error);
      showError(
        "Failed to delete document",
        error instanceof Error
          ? error.message
          : "Unknown error occurred while deleting the document."
      );
    }
  };

  const handleReprocessDocument = async (documentId: string): Promise<void> => {
    try {
      // For now, just show a message since we don't have reprocess endpoint
      showInfo(
        "Reprocessing not available",
        "Documents are processed automatically when uploaded to LlamaCloud"
      );
    } catch (error) {
      console.error("Failed to reprocess document:", error);
      showError(
        "Failed to reprocess document",
        error instanceof Error ? error.message : "Unknown error"
      );
    }
  };

  const filteredDocuments = (documents || []).filter((doc) => {
    // Add safety checks
    if (!doc || typeof doc !== "object") return false;
    if (!doc.name || typeof doc.name !== "string") return false;

    const matchesSearch = doc.name
      .toLowerCase()
      .includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === "all" || doc.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Debug logging
  useEffect(() => {
    console.log("🔍 Documents state:", {
      documentsLength: documents?.length || 0,
      filteredLength: filteredDocuments.length,
      searchQuery,
      statusFilter,
      sampleDoc: documents?.[0],
    });
  }, [documents, filteredDocuments.length, searchQuery, statusFilter]);

  const getStatusIcon = (status: DocumentStatus) => {
    switch (status) {
      case "success":
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case "error":
        return <AlertCircle className="w-4 h-4 text-red-500" />;
      case "processing":
      case "uploading":
      case "parsing":
      case "indexing":
        return <Clock className="w-4 h-4 text-yellow-500 animate-pulse" />;
      default:
        return <FileText className="w-4 h-4 text-muted-foreground" />;
    }
  };

  const getStatusText = (status: DocumentStatus) => {
    switch (status) {
      case "uploading":
        return "Uploading...";
      case "parsing":
        return "Parsing...";
      case "indexing":
        return "Indexing...";
      case "processing":
        return "Processing...";
      case "success":
        return "Ready";
      case "error":
        return "Error";
      default:
        return status;
    }
  };

  // Chat functionality
  const handleSendMessage = async () => {
    if (!inputMessage.trim() || isSending) return;

    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      role: "user",
      content: inputMessage,
      timestamp: new Date().toISOString(),
    };

    setChatMessages((prev) => [...prev, userMessage]);
    setInputMessage("");
    setIsSending(true);
    setChatError(null);

    try {
      const response = await knowledgeService.chatWithPineconeRAG({
        message: inputMessage,
        selectedDocuments: selectedDocuments,
        systemMessage: DEFAULT_SYSTEM_MESSAGE,
        temperature: 0.7,
        maxTokens: 2048,
      });

      const assistantMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        role: "assistant",
        content:
          response.message || "I'm here to help you with your documents!",
        timestamp: new Date().toISOString(),
        sources: response.sources || [],
        metadata: response.metadata,
      };

      setChatMessages((prev) => [...prev, assistantMessage]);
    } catch (error) {
      console.error("Chat error:", error);
      setChatError("Failed to get response. Please try again.");

      const errorMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        role: "assistant",
        content: "Sorry, I encountered an error. Please try again.",
        timestamp: new Date().toISOString(),
      };
      setChatMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const toggleDocumentSelection = (documentId: string) => {
    setSelectedDocuments((prev) =>
      prev.includes(documentId)
        ? prev.filter((id) => id !== documentId)
        : [...prev, documentId]
    );
  };

  const clearChat = () => {
    setChatMessages([]);
    setChatError(null);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto p-6">
        {/* Header - CRM Style */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Knowledge Base
            </h1>
            <p className="text-gray-600 text-lg">
              Upload documents and chat with your knowledge base
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={loadDocuments}
              disabled={isLoading}
              className="flex items-center gap-2 px-4 py-2 text-sm border border-gray-300 hover:border-gray-400 rounded-md transition-colors"
            >
              <RefreshCw
                className={`w-4 h-4 ${isLoading ? "animate-spin" : ""}`}
              />
              {isLoading ? "Loading..." : "Refresh"}
            </button>
          </div>
        </div>

        {/* Tab Navigation - CRM Style */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab("upload")}
              className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === "upload"
                  ? "border-blue-500 text-blue-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              <div className="flex items-center space-x-2">
                <Upload className="w-4 h-4" />
                <span>Upload</span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab("chat")}
              className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === "chat"
                  ? "border-blue-500 text-blue-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              <div className="flex items-center space-x-2">
                <MessageSquare className="w-4 h-4" />
                <span>Chat with Data</span>
              </div>
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        <AnimatePresence mode="wait">
          {activeTab === "upload" && (
            <motion.div
              key="upload"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="space-y-8"
            >
              {/* Stats Cards - CRM Style */}
              {stats && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-600">
                          Total Documents
                        </p>
                        <p className="text-2xl font-bold text-gray-900">
                          {stats.totalDocuments}
                        </p>
                      </div>
                      <div className="p-2 bg-blue-100 rounded-lg">
                        <Database className="w-6 h-6 text-blue-600" />
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-600">
                          Total Size
                        </p>
                        <p className="text-2xl font-bold text-gray-900">
                          {knowledgeService.formatFileSize(stats.totalSize)}
                        </p>
                      </div>
                      <div className="p-2 bg-green-100 rounded-lg">
                        <FileText className="w-6 h-6 text-green-600" />
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-600">
                          Processing
                        </p>
                        <p className="text-2xl font-bold text-gray-900">
                          {stats.processingCount}
                        </p>
                      </div>
                      <div className="p-2 bg-yellow-100 rounded-lg">
                        <Clock className="w-6 h-6 text-yellow-600" />
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-600">
                          Errors
                        </p>
                        <p className="text-2xl font-bold text-gray-900">
                          {stats.errorCount}
                        </p>
                      </div>
                      <div className="p-2 bg-red-100 rounded-lg">
                        <AlertCircle className="w-6 h-6 text-red-600" />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Documents List - Enhanced for 100+ documents */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="p-6 border-b border-gray-200">
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                        <FileText className="w-5 h-5 text-blue-600" />
                        Your Documents
                      </h3>
                      <p className="text-sm text-gray-500 mt-1">
                        {filteredDocuments.length} of {documents?.length || 0}{" "}
                        documents
                      </p>
                    </div>

                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                      {/* Upload Button - Matching New Proposal button style */}
                      <button
                        onClick={() => setShowUploadDialog(true)}
                        disabled={isUploading}
                        className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium"
                      >
                        <Upload className="w-4 h-4" />
                        {isUploading ? "Uploading..." : "Upload"}
                      </button>

                      {/* Search */}
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          type="text"
                          placeholder="Search documents..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="w-64 pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                        />
                      </div>

                      {/* Filter */}
                      <select
                        value={statusFilter}
                        onChange={(e) =>
                          setStatusFilter(
                            e.target.value as DocumentStatus | "all"
                          )
                        }
                        className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm bg-white"
                      >
                        <option value="all">All Status</option>
                        <option value="success">Ready</option>
                        <option value="processing">Processing</option>
                        <option value="error">Error</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Documents Grid - Enhanced for 100+ documents */}
                <div className="p-6">
                  {isLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <RefreshCw className="w-6 h-6 animate-spin text-blue-600" />
                      <span className="ml-3 text-gray-600">
                        Loading documents...
                      </span>
                    </div>
                  ) : filteredDocuments.length === 0 ? (
                    <div className="text-center py-12">
                      <FileText className="w-16 w-16 text-gray-400 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-gray-900 mb-2">
                        No documents found
                      </h3>
                      <p className="text-gray-500 mb-4">
                        {searchQuery || statusFilter !== "all"
                          ? "Try adjusting your search or filter criteria."
                          : "Upload your first document to get started."}
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-3 max-h-96 overflow-y-auto">
                      {filteredDocuments.map((doc, index) => (
                        <div
                          key={doc.id}
                          className="group p-4 border border-gray-200 rounded-lg hover:shadow-md hover:border-gray-300 transition-all duration-200 bg-white"
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3 flex-1 min-w-0">
                              <div className="flex-shrink-0">
                                {getStatusIcon(doc.status)}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <h4 className="text-sm font-medium text-gray-900 truncate">
                                    {doc.name}
                                  </h4>
                                  <span className="text-xs text-gray-400">
                                    #{index + 1}
                                  </span>
                                </div>
                                <div className="flex items-center space-x-4 mt-1">
                                  <span className="text-xs text-gray-500">
                                    {knowledgeService.formatFileSize(
                                      doc.size || 0
                                    )}
                                  </span>
                                  <span className="text-xs text-gray-500">
                                    {new Date(
                                      doc.uploadedAt || ""
                                    ).toLocaleDateString()}
                                  </span>
                                </div>
                              </div>
                            </div>

                            {/* Actions */}
                            <div className="flex items-center space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
                              {doc.status === "success" && (
                                <button
                                  onClick={() =>
                                    toggleDocumentSelection(doc.id)
                                  }
                                  className={`px-3 py-1 text-xs rounded-full transition-colors ${
                                    selectedDocuments.includes(doc.id)
                                      ? "bg-blue-600 text-white"
                                      : "bg-gray-100 hover:bg-gray-200 text-gray-700"
                                  }`}
                                >
                                  {selectedDocuments.includes(doc.id)
                                    ? "Selected"
                                    : "Select"}
                                </button>
                              )}

                              {doc.status === "error" && (
                                <button
                                  onClick={() =>
                                    handleReprocessDocument(doc.id)
                                  }
                                  className="px-3 py-1 text-xs bg-yellow-100 text-yellow-700 rounded-full hover:bg-yellow-200 transition-colors"
                                >
                                  Retry
                                </button>
                              )}

                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteDocument(doc.id);
                                }}
                                className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                title="Delete document"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Bulk Actions - CRM Style */}
                {filteredDocuments.length > 0 && (
                  <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                      <div className="text-sm text-gray-600">
                        <span className="font-medium">
                          {filteredDocuments.length}
                        </span>{" "}
                        of{" "}
                        <span className="font-medium">
                          {documents?.length || 0}
                        </span>{" "}
                        documents
                        {selectedDocuments.length > 0 && (
                          <span className="ml-2 text-blue-600">
                            • {selectedDocuments.length} selected
                          </span>
                        )}
                      </div>
                      <div className="flex items-center space-x-3">
                        <button
                          onClick={() => {
                            const readyDocs = filteredDocuments.filter(
                              (doc) => doc.status === "success"
                            );
                            setSelectedDocuments(
                              readyDocs.map((doc) => doc.id)
                            );
                          }}
                          className="px-3 py-1.5 text-sm bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          disabled={
                            filteredDocuments.filter(
                              (doc) => doc.status === "success"
                            ).length === 0
                          }
                        >
                          Select All Ready
                        </button>
                        <button
                          onClick={() => setSelectedDocuments([])}
                          className="px-3 py-1.5 text-sm bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          disabled={selectedDocuments.length === 0}
                        >
                          Clear Selection
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {activeTab === "chat" && (
            <motion.div
              key="chat"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="space-y-6"
            >
              {/* Chat Container with fixed height to prevent footer override */}
              <div className="h-[600px] w-full">
                <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 h-full">
                  {/* Document Selection Sidebar */}
                  <div className="lg:col-span-1">
                    <div className="dashboard-card p-4 h-full flex flex-col">
                      <h3 className="font-semibold mb-4 flex items-center space-x-2">
                        <FileText className="w-5 h-5" />
                        <span>Select Documents</span>
                      </h3>

                      {/* Search */}
                      <div className="relative mb-4">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <input
                          type="text"
                          placeholder="Search documents..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="w-full pl-10 pr-4 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all text-sm"
                        />
                      </div>

                      {/* Documents List */}
                      <div className="flex-1 overflow-y-auto space-y-2 mb-4 max-h-[350px]">
                        {isLoading ? (
                          <div className="flex items-center justify-center py-8">
                            <div className="loading-spinner w-5 h-5"></div>
                            <span className="ml-2 text-sm text-muted-foreground">
                              Loading...
                            </span>
                          </div>
                        ) : filteredDocuments.length === 0 ? (
                          <div className="text-center py-8">
                            <FileText className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                            <p className="text-sm text-muted-foreground">
                              No documents found
                            </p>
                            <button
                              onClick={() => setActiveTab("upload")}
                              className="text-xs text-primary hover:underline mt-2"
                            >
                              Upload documents first
                            </button>
                          </div>
                        ) : (
                          filteredDocuments.map((doc) => (
                            <div
                              key={doc.id}
                              className={`p-3 rounded-lg border cursor-pointer transition-all hover:shadow-sm ${
                                selectedDocuments.includes(doc.id)
                                  ? "border-primary bg-primary/5"
                                  : "border-border hover:border-primary/50"
                              }`}
                              onClick={() => toggleDocumentSelection(doc.id)}
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-2 flex-1 min-w-0">
                                  <div className="flex-shrink-0">
                                    {getStatusIcon(doc.status)}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <h4 className="text-sm font-medium truncate">
                                      {doc.name}
                                    </h4>
                                    <p className="text-xs text-muted-foreground">
                                      {getStatusText(doc.status)}
                                    </p>
                                  </div>
                                </div>
                                {selectedDocuments.includes(doc.id) && (
                                  <CheckCircle className="w-4 h-4 text-primary flex-shrink-0" />
                                )}
                              </div>
                            </div>
                          ))
                        )}
                      </div>

                      {/* Selected Count */}
                      {selectedDocuments.length > 0 && (
                        <div className="p-2 bg-primary/10 rounded-lg mb-4">
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-primary font-medium">
                              {selectedDocuments.length} selected
                            </span>
                            <button
                              onClick={() => setSelectedDocuments([])}
                              className="text-xs text-primary hover:underline"
                            >
                              Clear all
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Quick Actions */}
                      <div className="border-t border-border pt-4">
                        <h4 className="text-sm font-medium mb-2">
                          Quick Actions
                        </h4>
                        <div className="space-y-2">
                          <button
                            onClick={() =>
                              setSelectedDocuments(
                                filteredDocuments.map((doc) => doc.id)
                              )
                            }
                            className="w-full text-xs text-left px-2 py-1 text-primary hover:bg-primary/10 rounded"
                            disabled={filteredDocuments.length === 0}
                          >
                            Select All Documents
                          </button>
                          <button
                            onClick={() => {
                              const readyDocs = filteredDocuments.filter(
                                (doc) => doc.status === "success"
                              );
                              setSelectedDocuments(
                                readyDocs.map((doc) => doc.id)
                              );
                            }}
                            className="w-full text-xs text-left px-2 py-1 text-primary hover:bg-primary/10 rounded"
                          >
                            Select Ready Documents
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Chat Interface */}
                  <div className="lg:col-span-3">
                    <div className="dashboard-card h-full flex flex-col">
                      {/* Chat Header */}
                      <div className="flex items-center justify-between p-4 border-b border-border flex-shrink-0">
                        <div className="flex items-center space-x-3">
                          <div className="p-2 bg-primary/10 rounded-lg">
                            <Brain className="w-5 h-5 text-primary" />
                          </div>
                          <div>
                            <h3 className="font-semibold">AI Assistant</h3>
                            <p className="text-sm text-muted-foreground">
                              {selectedDocuments.length > 0
                                ? `Chatting with ${selectedDocuments.length} document(s)`
                                : "Select documents to start chatting"}
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={clearChat}
                          className="text-sm text-muted-foreground hover:text-foreground px-3 py-1 rounded-lg hover:bg-muted/50"
                        >
                          Clear chat
                        </button>
                      </div>

                      {/* Chat Messages - Fixed height for footer space */}
                      <div className="flex-1 overflow-y-auto p-4 space-y-4 max-h-[400px]">
                        {chatMessages.length === 0 ? (
                          <div className="text-center py-8">
                            <MessageSquare className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                            <h3 className="text-lg font-semibold mb-2">
                              Start a conversation
                            </h3>
                            <p className="text-muted-foreground mb-4">
                              Select documents from the sidebar and ask
                              questions about them
                            </p>

                            {/* Suggested Questions */}
                            {selectedDocuments.length > 0 && (
                              <div className="mt-6 max-w-md mx-auto">
                                <p className="text-sm font-medium mb-3">
                                  Try asking:
                                </p>
                                <div className="space-y-2">
                                  {[
                                    "What are the key points in these documents?",
                                    "Can you summarize the main topics?",
                                    "What are the important dates mentioned?",
                                    "Are there any action items or recommendations?",
                                  ].map((question, index) => (
                                    <button
                                      key={index}
                                      onClick={() => setInputMessage(question)}
                                      className="block w-full text-left p-2 text-sm bg-muted hover:bg-muted/80 rounded-lg transition-colors"
                                    >
                                      "{question}"
                                    </button>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        ) : (
                          chatMessages.map((message) => (
                            <div
                              key={message.id}
                              className={`flex ${
                                message.role === "user"
                                  ? "justify-end"
                                  : "justify-start"
                              }`}
                            >
                              <div
                                className={`max-w-[85%] rounded-lg p-3 ${
                                  message.role === "user"
                                    ? "bg-primary text-primary-foreground"
                                    : "bg-muted"
                                }`}
                              >
                                <div className="flex items-start space-x-2">
                                  {message.role === "assistant" && (
                                    <Bot className="w-5 h-5 mt-1 text-primary flex-shrink-0" />
                                  )}
                                  <div className="flex-1">
                                    <p className="text-sm leading-relaxed whitespace-pre-wrap">
                                      {message.content}
                                    </p>
                                    {message.sources &&
                                      message.sources.length > 0 && (
                                        <div className="mt-2 pt-2 border-t border-border/50">
                                          <p className="text-xs text-muted-foreground">
                                            📚 Sources:{" "}
                                            {message.metadata?.documentsUsed &&
                                            message.metadata.documentsUsed
                                              .length > 0
                                              ? message.metadata.documentsUsed.join(
                                                  ", "
                                                )
                                              : `${
                                                  message.metadata
                                                    ?.uniqueDocuments || 1
                                                } document(s)`}
                                            <span className="ml-2 opacity-70">
                                              ({message.sources.length} chunks)
                                            </span>
                                          </p>
                                        </div>
                                      )}
                                    <p className="text-xs text-muted-foreground mt-1">
                                      {new Date(
                                        message.timestamp
                                      ).toLocaleTimeString()}
                                    </p>
                                  </div>
                                  {message.role === "user" && (
                                    <User className="w-5 h-5 mt-1 flex-shrink-0" />
                                  )}
                                </div>
                              </div>
                            </div>
                          ))
                        )}
                        {isSending && (
                          <div className="flex justify-start">
                            <div className="bg-muted rounded-lg p-3">
                              <div className="flex items-center space-x-2">
                                <Bot className="w-5 h-5 text-primary" />
                                <div className="flex space-x-1">
                                  <div className="w-2 h-2 bg-primary rounded-full animate-bounce"></div>
                                  <div
                                    className="w-2 h-2 bg-primary rounded-full animate-bounce"
                                    style={{ animationDelay: "0.1s" }}
                                  ></div>
                                  <div
                                    className="w-2 h-2 bg-primary rounded-full animate-bounce"
                                    style={{ animationDelay: "0.2s" }}
                                  ></div>
                                </div>
                                <span className="text-sm text-muted-foreground">
                                  AI is thinking...
                                </span>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Chat Input - Fixed at bottom */}
                      <div className="p-4 border-t border-border bg-background flex-shrink-0">
                        {chatError && (
                          <div className="mb-3 p-2 bg-destructive/10 text-destructive text-sm rounded-lg flex items-center justify-between">
                            <span>{chatError}</span>
                            <button
                              onClick={() => setChatError(null)}
                              className="text-destructive hover:text-destructive/80"
                            >
                              ✕
                            </button>
                          </div>
                        )}

                        <div className="flex space-x-3">
                          <div className="flex-1 relative">
                            <textarea
                              value={inputMessage}
                              onChange={(e) => setInputMessage(e.target.value)}
                              onKeyPress={handleKeyPress}
                              placeholder={
                                selectedDocuments.length > 0
                                  ? "Ask a question about your documents... (Shift+Enter for new line)"
                                  : "Select documents first to start chatting..."
                              }
                              className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all resize-none text-sm"
                              disabled={
                                isSending || selectedDocuments.length === 0
                              }
                              rows={1}
                              style={{ minHeight: "40px", maxHeight: "80px" }}
                            />
                          </div>
                          <button
                            onClick={handleSendMessage}
                            disabled={
                              !inputMessage.trim() ||
                              isSending ||
                              selectedDocuments.length === 0
                            }
                            className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
                          >
                            <Send className="w-4 h-4" />
                          </button>
                        </div>

                        {/* Quick action buttons */}
                        <div className="flex flex-wrap gap-2 mt-2">
                          {[
                            "Summarize",
                            "Key points",
                            "Action items",
                            "Important dates",
                          ].map((action) => (
                            <button
                              key={action}
                              onClick={() => setInputMessage(`${action}: `)}
                              className="px-2 py-1 text-xs bg-muted hover:bg-muted/80 rounded-full transition-colors"
                              disabled={selectedDocuments.length === 0}
                            >
                              {action}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Upload Dialog */}
        {showUploadDialog && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                {/* Enhanced Header with Close Button */}
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="bg-primary/10 p-2 rounded-lg">
                      <Upload className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <h2 className="text-xl font-semibold text-gray-900">Upload Documents</h2>
                      <p className="text-sm text-gray-600">Add documents to your knowledge base</p>
                    </div>
                  </div>
                  <button
                    onClick={closeDialog}
                    className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 p-2 rounded-lg transition-colors"
                    aria-label="Close dialog"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>

                {/* Enhanced Upload Configuration */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                  {/* Enhanced Company Selection */}
                  <div className="space-y-3">
                    <label className="block text-sm font-medium text-gray-700">
                      <Building2 className="w-4 h-4 inline mr-2 text-primary" />
                      Select Company 
                    </label>
                    <div className="relative">
                      {/* Company Search Input */}
                      <div className="relative mb-2">
                        <input
                          type="text"
                          placeholder="Search companies..."
                          value={companySearchQuery}
                          onChange={(e) => setCompanySearchQuery(e.target.value)}
                          className="w-full px-3 py-2 pl-9 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary text-sm"
                        />
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                      </div>

                      {/* Company Dropdown */}
                      <div className="relative">
                        <select
                          value={selectedCompanyId}
                          onChange={(e) => setSelectedCompanyId(e.target.value)}
                          className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary appearance-none bg-white text-sm"
                          disabled={loadingCompanies}
                        >
                          <option value="">
                            {loadingCompanies
                              ? "Loading companies..."
                              : companies.length === 0
                              ? "No companies available"
                              : "Choose a company"}
                          </option>
                          {companies
                            .filter((company) =>
                              company.name.toLowerCase().includes(companySearchQuery.toLowerCase())
                            )
                            .map((company) => (
                              <option key={company.id} value={company.id}>
                                {company.name}
                              </option>
                            ))}
                        </select>
                        <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                      </div>
                    </div>

                    {/* Company Selection Feedback */}
                    {!selectedCompanyId && (
                      <p className="text-xs text-blue-600 flex items-center gap-1">
                        <Info className="w-3 h-3" />
                        No company selected - documents will be saved as default
                      </p>
                    )}

                    {selectedCompanyId && (
                      <p className="text-xs text-green-600 flex items-center gap-1">
                        <Check className="w-3 h-3" />
                        Company selected successfully
                      </p>
                    )}
                  </div>

                  {/* Enhanced Document Description */}
                  <div className="space-y-3">
                    <label className="block text-sm font-medium text-gray-700">
                      <FileText className="w-4 h-4 inline mr-2 text-primary" />
                      Document Description
                    </label>
                    <textarea
                      value={documentDescription}
                      onChange={(e) => setDocumentDescription(e.target.value)}
                      placeholder="Enter a brief description of this document (e.g., Project proposal, Technical specification, Contract agreement, etc.)"
                      className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary resize-none text-sm"
                      rows={4}
                    />
                    <p className="text-xs text-gray-500 flex items-start gap-1">
                      <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
                      Add a description to help identify and organize your documents
                    </p>
                  </div>
                </div>

                {/* Category and Sub-category Configuration */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                  {/* Category Selection */}
                  <div className="space-y-3">
                    <label className="block text-sm font-medium text-gray-700">
                      <Filter className="w-4 h-4 inline mr-2 text-primary" />
                      Category
                    </label>
                    <select
                      value={documentCategory}
                      onChange={(e) => setDocumentCategory(e.target.value)}
                      className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary text-sm"
                    >
                      <option value="">Select Category</option>
                      <option value="general">General</option>
                      <option value="contracts">Contracts</option>
                      <option value="proposals">Proposals</option>
                      <option value="technical">Technical</option>
                      <option value="financial">Financial</option>
                      <option value="legal">Legal</option>
                      <option value="marketing">Marketing</option>
                      <option value="hr">Human Resources</option>
                      <option value="operations">Operations</option>
                      <option value="research">Research</option>
                    </select>
                    <p className="text-xs text-gray-500 flex items-start gap-1">
                      <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
                      Choose a category to organize your documents
                    </p>
                  </div>

                  {/* Sub-category Selection */}
                  <div className="space-y-3">
                    <label className="block text-sm font-medium text-gray-700">
                      <Filter className="w-4 h-4 inline mr-2 text-primary" />
                      Sub-category
                    </label>
                    <select
                      value={documentSubCategory}
                      onChange={(e) => setDocumentSubCategory(e.target.value)}
                      className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary text-sm"
                    >
                      <option value="">Select Sub-category</option>
                      <option value="uncategorized">Uncategorized</option>
                      <option value="draft">Draft</option>
                      <option value="final">Final</option>
                      <option value="template">Template</option>
                      <option value="reference">Reference</option>
                      <option value="archive">Archive</option>
                      <option value="urgent">Urgent</option>
                      <option value="confidential">Confidential</option>
                      <option value="public">Public</option>
                      <option value="internal">Internal</option>
                    </select>
                    <p className="text-xs text-gray-500 flex items-start gap-1">
                      <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
                      Add a sub-category for better organization
                    </p>
                  </div>
                </div>

                {/* Enhanced Upload Zone */}
                <div
                  {...getRootProps()}
                  className={`border-2 border-dashed rounded-lg p-6 text-center transition-all duration-300 cursor-pointer ${
                    isDragActive
                      ? "border-primary bg-primary/5"
                      : "border-gray-300 hover:border-primary/60 hover:bg-gray-50"
                  } ${isUploading ? "pointer-events-none opacity-80" : ""}`}
                >
                  <input {...getInputProps()} />
                  <div className="space-y-4">
                    <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
                      <Upload className="w-8 h-8 text-primary" />
                    </div>
                    <div className="space-y-2">
                      <h3 className="text-lg font-medium text-gray-900">
                        {isDragActive
                          ? "Drop files here"
                          : "Upload Documents"}
                      </h3>
                      <p className="text-sm text-gray-600">
                        Drag and drop files here, or click to browse
                      </p>
                      <p className="text-xs text-gray-500">
                        Supports PDF, Word (.doc/.docx), Text (.txt), Markdown (.md), RTF, ODT • Max 50MB per file
                      </p>
                    </div>
                    {!isDragActive && !isUploading && (
                      <button
                        type="button"
                        className="px-6 py-2.5 bg-primary text-white rounded-md font-medium hover:bg-primary/90 transition-colors text-sm shadow-sm"
                      >
                        Choose Files
                      </button>
                    )}
                  </div>
                </div>

                {/* Enhanced Upload Progress */}
                {(isUploading ||
                  (uploadProgress &&
                    Object.keys(uploadProgress).length > 0)) && (
                  <div className="mt-6 space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="bg-primary/10 p-1.5 rounded-lg">
                          <Upload className="w-4 h-4 text-primary" />
                        </div>
                        <h4 className="text-sm font-medium text-gray-900">
                          Upload Progress
                        </h4>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                          {uploadProgress
                            ? Object.keys(uploadProgress).length
                            : 0}{" "}
                          file(s)
                        </div>

                        {/* Cancel Upload Button */}
                        {isUploading && (
                          <button
                            onClick={cancelUpload}
                            className="text-xs text-red-600 hover:text-red-700 flex items-center gap-1 px-2 py-1 hover:bg-red-50 rounded-full transition-colors"
                          >
                            <X className="w-3 h-3" />
                            Cancel
                          </button>
                        )}
                      </div>
                    </div>

                    {uploadProgress &&
                      Object.entries(uploadProgress).map(
                        ([documentId, progress]) => (
                          <div
                            key={documentId}
                            className="space-y-2 p-4 bg-gray-50 rounded-lg border border-gray-100"
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2 flex-1 min-w-0">
                                <div
                                  className={`w-2 h-2 rounded-full flex-shrink-0 ${
                                    progress.status === "uploading"
                                      ? "bg-primary animate-pulse"
                                      : progress.status === "processing"
                                      ? "bg-yellow-500 animate-pulse"
                                      : progress.status === "success"
                                      ? "bg-green-500"
                                      : "bg-red-500"
                                  }`}
                                />
                                <div className="flex-1 min-w-0">
                                  <p className="font-medium text-gray-900 text-sm truncate">
                                    {progress.message || "Processing..."}
                                  </p>
                                  <p className="text-xs text-gray-500 capitalize">
                                    {progress.status}
                                  </p>
                                </div>
                              </div>
                              <div className="text-right flex-shrink-0">
                                <div className="text-sm font-medium text-gray-900">
                                  {Math.round(progress.progress || 0)}%
                                </div>
                              </div>
                            </div>

                            <Progress
                              value={progress.progress || 0}
                              className="h-2"
                            />
                          </div>
                        )
                      )}

                    {isUploading &&
                      (!uploadProgress ||
                        Object.keys(uploadProgress).length === 0) && (
                        <div className="space-y-2 p-4 bg-gray-50 rounded-lg border border-gray-100">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <div className="w-2 h-2 rounded-full bg-primary animate-pulse" />
                              <p className="font-medium text-gray-900 text-sm">
                                Initializing upload...
                              </p>
                            </div>
                            <button
                              onClick={cancelUpload}
                              className="text-xs text-red-600 hover:text-red-700 flex items-center gap-1"
                            >
                              <X className="w-3 h-3" />
                              Cancel
                            </button>
                          </div>
                          <Progress value={25} className="h-2" />
                        </div>
                      )}
                  </div>
                )}

                {/* Enhanced Dialog Footer */}
                <div className="mt-8 pt-6 border-t border-gray-200">
                  <div className="flex items-center justify-between">
                    {/* Upload Status */}
                    <div className="flex items-center gap-2">
                      {!isUploading &&
                        uploadProgress &&
                        Object.keys(uploadProgress).length > 0 && (
                          <div className="flex items-center gap-2">
                            {Object.values(uploadProgress).every(
                              (p) => p.status === "success"
                            ) ? (
                              <>
                                <CheckCircle className="w-4 h-4 text-green-600" />
                                <span className="text-sm text-green-600 font-medium">
                                  All uploads completed successfully!
                                </span>
                              </>
                            ) : Object.values(uploadProgress).some(
                                (p) => p.status === "error"
                              ) ? (
                              <>
                                <AlertCircle className="w-4 h-4 text-red-600" />
                                <span className="text-sm text-red-600 font-medium">
                                  Some uploads failed
                                </span>
                              </>
                            ) : (
                              <>
                                <Clock className="w-4 h-4 text-yellow-600" />
                                <span className="text-sm text-yellow-600 font-medium">
                                  Upload in progress...
                                </span>
                              </>
                            )}
                          </div>
                        )}
                    </div>

                    {/* Action Buttons */}
                    <div className="flex items-center gap-3">
                      {/* Cancel Button */}
                      <button
                        onClick={closeDialog}
                        disabled={isUploading}
                        className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isUploading ? "Uploading..." : "Cancel"}
                      </button>

                      {/* Close Button (when upload is complete) */}
                      {!isUploading &&
                        uploadProgress &&
                        Object.keys(uploadProgress).length > 0 &&
                        Object.values(uploadProgress).every(
                          (p) => p.status === "success" || p.status === "error"
                        ) && (
                          <button
                            onClick={closeDialog}
                            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors text-sm font-medium"
                          >
                            Close
                          </button>
                        )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AIAssistant;
